import { useFairePayEnrolmentInfo } from "@faire/deprecated-retailer-visitor-shared/hooks/fairePay/useFairePayEnrolmentInfo";
import { bpsToPercent } from "@faire/deprecated-retailer-visitor-shared/lib/bpsToDecimalAndPercent";
import { useQuery as useTotalReward } from "@faire/web-api--source/endpoints/www/api/faire-pay/total-reward/get-hooks";
import { useQuery as useFairePayInfoQuery } from "@faire/web-api--source/endpoints/www/api/faire-pay/v2/info/get-hooks";
import { IFairePay } from "@faire/web-api--source/indigofair/data/IFairePay";
import { IFairePayEnrollmentInfo } from "@faire/web-api--source/indigofair/data/IFairePayEnrollmentInfo";
import {
  AllowedFormats,
  intlDateWithTimezone,
} from "@faire/web/common/localization/date-fns/intlDate";
import { useFormatMoney } from "@faire/web/common/money";
import { useCallback, useMemo } from "react";

export const useFairePayInfo = (preventAutoRun?: boolean) => {
  const { enrolmentInfo, isPending: isEnrolmentInfoPending } =
    useFairePayEnrolmentInfo();
  const { formatMoney } = useFormatMoney();

  const enabled =
    !preventAutoRun &&
    enrolmentInfo?.status === IFairePayEnrollmentInfo.Status.ENROLLED;

  const {
    data: totalRewardInfo,
    isPending: isTotalRewardPending,
    refetch: refetchTotalReward,
  } = useTotalReward({ enabled });

  const {
    data: fairePayV2Info,
    isPending: isFairePayInfoPending,
    refetch: refetchFairePayV2Info,
  } = useFairePayInfoQuery({
    enabled,
  });

  const {
    next_payment_due_at,
    time_zone,
    enrolment_state,
    current_period_end_at,
    yearly_interest_rate_bps,
    current_balance,
  } = fairePayV2Info?.faire_pay_info ?? {};

  const nextPaymentDueDateWithFormatting = (
    format: AllowedFormats
  ): string | undefined => {
    if (!next_payment_due_at || !time_zone) {
      return undefined;
    }

    return intlDateWithTimezone(next_payment_due_at, format, time_zone);
  };

  const currentPeriodEndDateWithFormatting = (): string | undefined => {
    if (!current_period_end_at || !time_zone) {
      return undefined;
    }

    return intlDateWithTimezone(current_period_end_at, "MMMM d", time_zone);
  };

  const formattedCurrentBalance = useMemo(() => {
    if (!current_balance) {
      return undefined;
    }
    return formatMoney({ money: current_balance, omitCentsIfZero: true });
  }, [current_balance, formatMoney]);

  const refetch = useCallback(async () => {
    await refetchFairePayV2Info();
    await refetchTotalReward();
  }, [refetchFairePayV2Info, refetchTotalReward]);

  return {
    fairePayInfo: fairePayV2Info?.faire_pay_info,
    totalSavingsToDate: totalRewardInfo?.total_reward_amount,
    refetch,
    isPending:
      (enabled && (isFairePayInfoPending || isTotalRewardPending)) ||
      isEnrolmentInfoPending,
    nextPaymentDueDateWithFormatting,
    currentPeriodEndDateWithFormatting,
    showPreCheckoutUI:
      enrolmentInfo?.status !== IFairePayEnrollmentInfo.Status.ENROLLED ||
      enrolment_state === IFairePay.EnrolmentState.PRE_ENROLLED,
    isFullyEnrolled:
      enrolment_state === IFairePay.EnrolmentState.FULLY_ENROLLED,
    isPreEnrolledNotCheckedOut:
      enrolment_state === IFairePay.EnrolmentState.PRE_ENROLLED,
    isPreEnrolled:
      enrolment_state === IFairePay.EnrolmentState.PRE_ENROLLED ||
      enrolment_state === IFairePay.EnrolmentState.PRE_ENROLLED_CHECKED_OUT,
    isPreEnrolledCheckedOut:
      enrolment_state === IFairePay.EnrolmentState.PRE_ENROLLED_CHECKED_OUT,
    isClosedPendingPayoff:
      fairePayV2Info?.faire_pay_info?.state ===
      IFairePay.State.CLOSED_PENDING_PAYOFF,
    yearlyInterestRatePercentage: yearly_interest_rate_bps
      ? bpsToPercent(yearly_interest_rate_bps)
      : undefined,
    showSoftCreditCheck: fairePayV2Info?.show_soft_credit_check,
    displayCreditLimit: fairePayV2Info?.display_credit_limit,
    dashboardBanner: fairePayV2Info?.dashboard_banner,
    formattedCurrentBalance,
    isFairePayInfoPending,
  };
};
